(() => {
  'use strict';

  const GALLERY_SELECTOR = 'media-gallery';
  const IMAGE_SELECTOR = '.product-media__image';
  const PAN_STEP = 64;
  const galleryStates = new WeakMap();
  const activeStates = new Set();
  let nextInstance = 0;

  const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');

  const validImageUrl = (value) => {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, document.baseURI);
      return url.protocol === 'https:' || url.origin === location.origin ? url.href : null;
    } catch {
      return null;
    }
  };

  const sourceUrlFor = (image) => {
    if (!(image instanceof HTMLImageElement)) return null;
    const explicit = validImageUrl(image.dataset.maxResolution || image.getAttribute('data_max_resolution'));
    if (explicit) return explicit;
    const srcset = String(image.getAttribute('srcset') || '').split(',').map((entry) => {
      const match = entry.trim().match(/^(\S+)\s+(\d+(?:\.\d+)?)(w|x)$/);
      return match ? { url: validImageUrl(match[1]), score: Number(match[2]) * (match[3] === 'x' ? 10000 : 1) } : null;
    }).filter((entry) => entry?.url).sort((a, b) => b.score - a.score);
    return srcset[0]?.url || validImageUrl(image.getAttribute('src')) || validImageUrl(image.currentSrc);
  };

  const imageIdentity = (value) => {
    const valid = validImageUrl(value);
    if (!valid) return null;
    const url = new URL(valid);
    return url.origin + url.pathname;
  };

  const makeButton = (label, className, attributes = {}) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    Object.entries(attributes).forEach(([name, value]) => button.setAttribute(name, value));
    return button;
  };

  const enhanceGallery = (gallery, instance) => {
    if (!(gallery instanceof HTMLElement)) return;
    if (typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') return;
    const existingState = galleryStates.get(gallery);
    if (existingState?.intact()) {
      existingState.reconcile();
      return;
    }
    existingState?.destroy();
    delete gallery.dataset.cgauInspectReady;
    const controller = new AbortController();
    const on = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: controller.signal });
    gallery.dataset.cgauInspectReady = 'true';

    const tools = document.createElement('div');
    tools.className = 'cgau-inspect-tools';
    const launch = makeButton('Inspect detail', 'cgau-inspect-launch', {
      'aria-haspopup': 'dialog',
      'aria-controls': `cgau-inspect-dialog-${instance}`,
    });
    const availability = document.createElement('span');
    availability.className = 'cgau-inspect-availability';
    availability.setAttribute('role', 'status');
    availability.setAttribute('aria-live', 'polite');
    tools.append(launch, availability);

    const dialog = document.createElement('dialog');
    dialog.className = 'cgau-inspect-dialog';
    dialog.id = `cgau-inspect-dialog-${instance}`;
    dialog.setAttribute('aria-labelledby', `cgau-inspect-title-${instance}`);
    dialog.innerHTML = `
      <div class="cgau-inspect-shell">
        <header class="cgau-inspect-head">
          <div><h2 id="cgau-inspect-title-${instance}">Inspect detail</h2><p>Drag to move · Scroll or pinch to zoom</p></div>
          <button type="button" class="cgau-inspect-close" aria-label="Close detail viewer">Close</button>
        </header>
        <div class="cgau-inspect-stage">
          <div class="cgau-inspect-lens" tabindex="0" aria-label="Product image. Drag or use arrow keys to move. Use plus and minus to zoom, or Home to fit the whole image.">
            <img class="cgau-inspect-image" alt="">
            <p class="cgau-inspect-message" role="status" aria-live="polite"></p>
          </div>
        </div>
        <div class="cgau-inspect-toolbar">
          <div class="cgau-inspect-zoom" role="group" aria-label="Image zoom">
            <button type="button" data-cgau-zoom="out" aria-label="Zoom out">−</button>
            <input type="range" min="1" max="100" value="100" step="0.1" aria-label="Image zoom percentage">
            <button type="button" data-cgau-zoom="in" aria-label="Zoom in">+</button>
            <output class="cgau-inspect-percent" aria-label="Current zoom"></output>
          </div>
          <div class="cgau-inspect-presets" role="group" aria-label="Image size">
            <button type="button" data-cgau-zoom="fit">Fit image</button>
            <button type="button" data-cgau-zoom="actual">100%</button>
          </div>
          <div class="cgau-inspect-nav" role="group" aria-label="Product images">
            <button type="button" data-cgau-image="previous" aria-label="Previous image">←</button>
            <span data-cgau-inspect-count aria-live="polite"></span>
            <button type="button" data-cgau-image="next" aria-label="Next image">→</button>
          </div>
        </div>
        <footer class="cgau-inspect-foot">
          <span data-cgau-inspect-resolution></span>
          <a class="cgau-inspect-original" target="_blank" rel="noopener">Open original image ↗</a>
        </footer>
      </div>`;

    const existingZoom = gallery.querySelector(':scope > zoom-dialog');
    gallery.insertBefore(tools, existingZoom || null);
    gallery.append(dialog);

    const lens = dialog.querySelector('.cgau-inspect-lens');
    const image = dialog.querySelector('.cgau-inspect-image');
    const resolution = dialog.querySelector('[data-cgau-inspect-resolution]');
    const close = dialog.querySelector('.cgau-inspect-close');
    const message = dialog.querySelector('.cgau-inspect-message');
    const range = dialog.querySelector('input[type="range"]');
    const percent = dialog.querySelector('.cgau-inspect-percent');
    const count = dialog.querySelector('[data-cgau-inspect-count]');
    const original = dialog.querySelector('.cgau-inspect-original');
    const zoomButtons = [...dialog.querySelectorAll('[data-cgau-zoom]')];
    image.draggable = false;
    if (reduceMotion?.matches) dialog.dataset.cgauMotion = 'static';
    let selectedImage = null;
    let panX = 0;
    let panY = 0;
    let scale = 1;
    let fitScale = 1;
    let naturalWidth = 0;
    let naturalHeight = 0;
    let fitted = true;
    let ready = false;
    let gesture = null;
    const pointers = new Map();
    let images = [];
    let imageIndex = 0;
    let loadVersion = 0;
    let loadTimer;
    let frame;
    let savedOverflow = null;

    const galleryImages = () => {
      const candidates = [...gallery.querySelectorAll(IMAGE_SELECTOR)]
        .filter((candidate) => !candidate.closest('.dialog-zoomed-gallery, .cgau-inspect-dialog'));
      if (gallery.dataset.presentation !== 'carousel') return candidates;
      // Infinite carousels temporarily reorder slides. Thumbnail order stays stable.
      const ordered = [...gallery.querySelectorAll('.slideshow-controls__thumbnail img')].map((thumbnail) => {
        const key = imageIdentity(sourceUrlFor(thumbnail));
        return candidates.find((candidate) => key && imageIdentity(sourceUrlFor(candidate)) === key);
      }).filter(Boolean);
      return [...new Set([...ordered, ...candidates])];
    };

    const currentImage = () => {
      // The chosen thumbnail updates before the carousel's scroll animation ends.
      // Respect that selection even if the inspector opens during the transition.
      if (gallery.dataset.presentation === 'carousel') {
        const thumbnail = gallery.querySelector('.slideshow-controls__thumbnail[aria-selected="true"] img');
        if (thumbnail) {
          const key = imageIdentity(sourceUrlFor(thumbnail));
          return galleryImages().find((candidate) => key && imageIdentity(sourceUrlFor(candidate)) === key) || null;
        }
      }
      if (selectedImage?.isConnected) return selectedImage;
      const activeSlide = gallery.querySelector('slideshow-slide[aria-hidden="false"]:not([hidden])');
      return activeSlide ? activeSlide.querySelector(IMAGE_SELECTOR) : galleryImages()[0] || null;
    };

    const refreshAvailability = () => {
      const candidate = currentImage();
      const usable = candidate instanceof HTMLImageElement && Boolean(sourceUrlFor(candidate));
      launch.hidden = !usable;
      if (!usable) availability.textContent = '';
    };

    const applyPan = () => {
      if (!ready) return;
      const limitX = Math.max(0, (naturalWidth * scale - lens.clientWidth) / 2);
      const limitY = Math.max(0, (naturalHeight * scale - lens.clientHeight) / 2);
      panX = Math.max(-limitX, Math.min(limitX, panX));
      panY = Math.max(-limitY, Math.min(limitY, panY));
      image.style.width = `${naturalWidth * scale}px`;
      image.style.height = `${naturalHeight * scale}px`;
      image.style.transform = `translate(-50%, -50%) translate(${panX}px, ${panY}px)`;
      lens.classList.toggle('is-zoomed', limitX > 0 || limitY > 0);
      // Align the minimum with the slider step so End can reach exactly 100%.
      range.min = String(Math.floor(fitScale * 1000) / 10);
      range.value = String(scale * 100);
      range.setAttribute('aria-valuetext', `${Math.round(scale * 100)} percent of original size`);
      percent.textContent = `${Math.round(scale * 100)}%`;
      range.disabled = fitScale >= 1;
      zoomButtons.forEach((button) => {
        const action = button.dataset.cgauZoom;
        button.disabled = (action === 'out' && scale <= fitScale + .0001) || (action === 'in' && scale >= .9999);
        if (action === 'fit' || action === 'actual') button.setAttribute('aria-pressed', String(action === 'fit' ? fitted : !fitted && scale >= .9999));
      });
    };

    const zoomTo = (value, x = 0, y = 0) => {
      if (!ready) return;
      const next = Math.max(fitScale, Math.min(1, value));
      const ratio = next / scale;
      panX = x - (x - panX) * ratio;
      panY = y - (y - panY) * ratio;
      scale = next;
      fitted = scale <= fitScale + .0001;
      applyPan();
    };

    const measure = () => {
      if (!ready || !dialog.open) return;
      fitScale = Math.min(1, Math.max(1, lens.clientWidth - 24) / naturalWidth, Math.max(1, lens.clientHeight - 24) / naturalHeight);
      if (fitted) { scale = fitScale; panX = panY = 0; }
      else scale = Math.max(fitScale, Math.min(1, scale));
      applyPan();
    };

    const fit = () => {
      fitted = true;
      measure();
    };

    const clearGesture = () => {
      pointers.clear();
      gesture = null;
      lens.classList.remove('is-dragging');
    };

    const setLoading = (text) => {
      ready = false;
      clearGesture();
      image.hidden = true;
      message.textContent = text;
      range.disabled = true;
      zoomButtons.forEach((button) => { button.disabled = true; });
      percent.textContent = '';
      resolution.textContent = '';
    };

    const loadImage = () => {
      const source = images[imageIndex];
      const highResolutionUrl = source?.url;
      const version = ++loadVersion;
      clearTimeout(loadTimer);
      count.textContent = `${imageIndex + 1} / ${images.length}`;
      dialog.querySelector('.cgau-inspect-nav').hidden = images.length < 2;
      original.hidden = !highResolutionUrl;
      if (highResolutionUrl) original.href = highResolutionUrl;
      else original.removeAttribute('href');
      setLoading('Loading image…');
      lens.setAttribute('aria-busy', 'true');
      const fail = () => {
        if (version !== loadVersion) return;
        clearTimeout(loadTimer);
        lens.removeAttribute('aria-busy');
        message.textContent = 'Image could not load. Try another photo or open the original below.';
      };
      if (!source || !highResolutionUrl) { fail(); return; }
      const probe = new Image();
      probe.decoding = 'async';
      probe.onload = async () => {
        if (version !== loadVersion) return;
        if (!probe.naturalWidth || !probe.naturalHeight) { fail(); return; }
        image.src = highResolutionUrl;
        image.alt = source.alt || 'Selected product image';
        try { await image.decode(); } catch { /* The loaded probe already confirmed this exact source. */ }
        if (version !== loadVersion) return;
        clearTimeout(loadTimer);
        naturalWidth = probe.naturalWidth;
        naturalHeight = probe.naturalHeight;
        ready = true;
        fitted = true;
        panX = panY = 0;
        message.textContent = '';
        image.hidden = false;
        lens.removeAttribute('aria-busy');
        resolution.textContent = `${naturalWidth} × ${naturalHeight} original`;
        measure();
      };
      probe.onerror = fail;
      loadTimer = setTimeout(fail, 15000);
      probe.src = highResolutionUrl;
    };

    const unlockPage = () => {
      if (savedOverflow === null) return;
      document.documentElement.style.overflow = savedOverflow;
      savedOverflow = null;
    };

    const openInspector = () => {
      if (dialog.open) return;
      const selected = currentImage();
      images = galleryImages().map((candidate) => ({ element: candidate, url: sourceUrlFor(candidate), alt: candidate.alt })).filter((candidate) => candidate.url);
      if (!images.length) { availability.textContent = 'No product image is available to inspect.'; return; }
      imageIndex = Math.max(0, images.findIndex((candidate) => candidate.element === selected));
      availability.textContent = '';
      dialog.showModal();
      savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      loadImage();
      lens.focus({ preventScroll: true });
    };

    on(launch, 'click', openInspector);
    on(close, 'click', () => dialog.close());
    on(dialog, 'close', () => {
      loadVersion += 1;
      clearTimeout(loadTimer);
      clearGesture();
      ready = false;
      image.removeAttribute('src');
      panX = panY = 0;
      unlockPage();
      if (!reduceMotion?.matches) delete dialog.dataset.cgauMotion;
      launch.focus({ preventScroll: true });
    });
    let backdropDown = false;
    on(dialog, 'pointerdown', (event) => { backdropDown = event.target === dialog; });
    on(dialog, 'click', (event) => { if (backdropDown && event.target === dialog) dialog.close(); backdropDown = false; });
    on(dialog, 'keydown', (event) => {
      if (event.target !== lens) return;
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', '+', '=', '-', '0'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home' || event.key === '0') { fit(); return; }
      if (event.key === '+' || event.key === '=') { zoomTo(scale * 1.25); return; }
      if (event.key === '-') { zoomTo(scale / 1.25); return; }
      const step = event.shiftKey ? PAN_STEP * 3 : PAN_STEP;
      if (event.key === 'ArrowLeft') panX += step;
      if (event.key === 'ArrowRight') panX -= step;
      if (event.key === 'ArrowUp') panY += step;
      if (event.key === 'ArrowDown') panY -= step;
      applyPan();
    });
    zoomButtons.forEach((button) => on(button, 'click', () => {
      const action = button.dataset.cgauZoom;
      if (action === 'fit') fit();
      else zoomTo(action === 'actual' ? 1 : scale * (action === 'in' ? 1.25 : .8));
    }));
    on(range, 'input', () => zoomTo(Number(range.value) / 100));
    dialog.querySelectorAll('[data-cgau-image]').forEach((button) => on(button, 'click', () => {
      imageIndex = (imageIndex + (button.dataset.cgauImage === 'next' ? 1 : -1) + images.length) % images.length;
      loadImage();
    }));
    on(image, 'dragstart', (event) => event.preventDefault());

    const localPoint = (event) => {
      const rect = lens.getBoundingClientRect();
      return { x: event.clientX - rect.left - rect.width / 2, y: event.clientY - rect.top - rect.height / 2 };
    };
    const resetGesture = () => {
      const points = [...pointers.values()];
      gesture = points.length > 1 ? {
        x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2,
        distance: Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y)), scale, panX, panY,
      } : points.length ? { ...points[0], panX, panY } : null;
    };
    on(lens, 'pointerdown', (event) => {
      if (!ready || event.button !== 0) return;
      pointers.set(event.pointerId, localPoint(event));
      resetGesture();
      lens.setPointerCapture(event.pointerId);
      lens.focus({ preventScroll: true });
      lens.classList.add('is-dragging');
    });
    on(lens, 'pointermove', (event) => {
      if (!pointers.has(event.pointerId) || !gesture) return;
      pointers.set(event.pointerId, localPoint(event));
      const points = [...pointers.values()];
      if (points.length > 1) {
        const ratio = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) / gesture.distance;
        scale = Math.max(fitScale, Math.min(1, gesture.scale * ratio));
        const zoomRatio = scale / gesture.scale;
        panX = (points[0].x + points[1].x) / 2 - (gesture.x - gesture.panX) * zoomRatio;
        panY = (points[0].y + points[1].y) / 2 - (gesture.y - gesture.panY) * zoomRatio;
        fitted = scale <= fitScale + .0001;
      } else {
        panX = gesture.panX + points[0].x - gesture.x;
        panY = gesture.panY + points[0].y - gesture.y;
      }
      applyPan();
      resetGesture();
    });
    const endDrag = (event) => {
      pointers.delete(event.pointerId);
      resetGesture();
      if (!pointers.size) lens.classList.remove('is-dragging');
    };
    on(lens, 'pointerup', endDrag);
    on(lens, 'pointercancel', endDrag);
    on(lens, 'lostpointercapture', endDrag);
    on(lens, 'wheel', (event) => {
      event.preventDefault();
      const point = localPoint(event);
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? lens.clientHeight : 1);
      zoomTo(scale * Math.exp(-Math.max(-100, Math.min(100, delta)) * .005), point.x, point.y);
    }, { passive: false });
    on(lens, 'dblclick', (event) => {
      if (!fitted) { fit(); return; }
      const point = localPoint(event);
      zoomTo(1, point.x, point.y);
    });
    const resize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { clearGesture(); measure(); });
    };
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(lens);
    on(globalThis, 'resize', resize);

    on(gallery, 'click', (event) => {
      const container = event.target instanceof Element ? event.target.closest('.product-media-container') : null;
      const candidate = container?.querySelector(IMAGE_SELECTOR);
      if (candidate && !candidate.closest('.dialog-zoomed-gallery')) selectedImage = candidate;
      globalThis.requestAnimationFrame(refreshAvailability);
    }, { capture: true });
    on(gallery, 'focusin', (event) => {
      const container = event.target instanceof Element ? event.target.closest('.product-media-container') : null;
      const candidate = container?.querySelector(IMAGE_SELECTOR);
      if (candidate && !candidate.closest('.dialog-zoomed-gallery')) selectedImage = candidate;
    });

    const activeObserver = new MutationObserver((records) => {
      if (records.some((record) => record.attributeName === 'aria-hidden' || record.attributeName === 'aria-selected')) {
        selectedImage = null;
        refreshAvailability();
      }
    });
    const observeSlides = () => {
      activeObserver.disconnect();
      gallery.querySelectorAll('slideshow-slide').forEach((slide) => activeObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] }));
      gallery.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumbnail) => activeObserver.observe(thumbnail, { attributes: true, attributeFilter: ['aria-selected'] }));
    };
    observeSlides();
    refreshAvailability();
    const state = {
      gallery,
      intact: () => tools.parentNode === gallery && dialog.parentNode === gallery,
      reconcile: () => {
        gallery.dataset.cgauInspectReady = 'true';
        observeSlides();
        refreshAvailability();
      },
      destroy: () => {
        controller.abort();
        activeObserver.disconnect();
        resizeObserver?.disconnect();
        cancelAnimationFrame(frame);
        clearTimeout(loadTimer);
        loadVersion += 1;
        if (dialog.open) dialog.close();
        unlockPage();
        tools.remove();
        dialog.remove();
        delete gallery.dataset.cgauInspectReady;
        galleryStates.delete(gallery);
        activeStates.delete(state);
      },
    };
    galleryStates.set(gallery, state);
    activeStates.add(state);
  };

  const enhanceAll = () => {
    activeStates.forEach((state) => { if (!state.gallery.isConnected) state.destroy(); });
    document.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => enhanceGallery(gallery, ++nextInstance));
  };
  let reconcileQueued = false;
  const pageObserver = new MutationObserver((records) => {
    const relevant = records.some((record) => record.target instanceof Element && !record.target.closest('.cgau-inspect-dialog, .cgau-inspect-tools') && (record.target.matches('media-gallery') || record.target.closest('media-gallery')))
      || records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1 && (node.matches?.('media-gallery') || node.querySelector?.('media-gallery'))));
    if (!relevant || reconcileQueued) return;
    reconcileQueued = true;
    globalThis.requestAnimationFrame(() => { reconcileQueued = false; enhanceAll(); });
  });

  const init = () => {
    enhanceAll();
    pageObserver.observe(document.body, { childList: true, subtree: true });
    reduceMotion?.addEventListener?.('change', () => {
      document.querySelectorAll('.cgau-inspect-dialog').forEach((dialog) => {
        if (reduceMotion.matches || dialog.hasAttribute('open')) dialog.dataset.cgauMotion = 'static';
        else delete dialog.dataset.cgauMotion;
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
